/**
 * Extract a display filename from a storage URL.
 * Strips query/hash and common upload timestamp prefixes like `20240722_123456_`.
 * Matches mobile app cleaning behavior so admin shows the same name users uploaded.
 */
export const getFileNameFromUrl = url => {
  if (!url || typeof url !== 'string') return '';

  try {
    const pathname = new URL(url).pathname;
    const rawName = decodeURIComponent(pathname.split('/').filter(Boolean).pop() || '');
    return cleanUploadedFileName(rawName);
  } catch {
    const segment = url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
    try {
      return cleanUploadedFileName(decodeURIComponent(segment));
    } catch {
      return cleanUploadedFileName(segment);
    }
  }
};

export const cleanUploadedFileName = fileName => {
  if (!fileName) return '';
  // Mobile uploads often prefix: YYYYMMDD_HHMMSS_originalname.ext
  const cleaned = fileName.replace(/^\d{8}_\d{6}_/, '');
  return cleaned || fileName;
};

export const getFileExtension = urlOrName => {
  const name = getFileNameFromUrl(urlOrName) || String(urlOrName || '');
  const parts = name.split('.');
  if (parts.length < 2) return '';
  return parts
    .pop()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'heic',
  'heif',
  'avif',
  'tif',
  'tiff',
  'svg',
]);

export const isImageFile = urlOrName => {
  const ext = getFileExtension(urlOrName);
  return IMAGE_EXTENSIONS.has(ext);
};

export const isPdfFile = urlOrName => getFileExtension(urlOrName) === 'pdf';

export const getDocumentDisplayName = (url, baseName = 'Document') => {
  const nameWithoutExt = String(baseName || 'Document').replace(/\.[^.]+$/, '');
  const ext = getFileExtension(url);
  return ext ? `${nameWithoutExt}.${ext}` : nameWithoutExt;
};

const escapeHtmlAttr = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;');

const openHtmlPreviewTab = html => {
  const win = window.open('', '_blank');
  if (!win) {
    console.warn('[DocumentView] window.open blocked (popup blocker?)');
    return null;
  }
  console.log('[DocumentView] opened blank preview tab');
  win.document.write(html);
  win.document.close();
  return win;
};

export const getPdfViewerUrl = url =>
  `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;

export const getDocumentViewHref = url => {
  if (!url) return '#';
  // Images preview via HTML; PDFs via Google Viewer (Azure CORS blocks browser fetch).
  if (isImageFile(url)) {
    return url;
  }
  if (isPdfFile(url)) {
    return getPdfViewerUrl(url);
  }
  return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(url)}`;
};

const openImagePreview = (url, displayName) => {
  console.log('[DocumentView] image preview path', { url, displayName });
  const safeUrl = escapeHtmlAttr(url);
  const safeTitle = escapeHtmlAttr(displayName).replace(/>/g, '&gt;');

  openHtmlPreviewTab(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>
      html, body { margin: 0; width: 100%; height: 100%; background: #111; }
      body { display: flex; align-items: center; justify-content: center; }
      img { max-width: 100%; max-height: 100vh; object-fit: contain; }
    </style>
  </head>
  <body>
    <img src="${safeUrl}" alt="${safeTitle}" />
  </body>
</html>`);
};

const openPdfInGoogleViewer = (url, displayName) => {
  const viewerUrl = getPdfViewerUrl(url);
  console.log('[DocumentView] opening PDF in Google Viewer (avoids Azure CORS / download)', {
    displayName,
    url,
    viewerUrl,
  });
  const win = window.open(viewerUrl, '_blank', 'noopener,noreferrer');
  if (!win) {
    console.warn('[DocumentView] Google Viewer popup blocked');
  }
  return win;
};

/**
 * Open document in a new tab.
 * - Images: HTML preview tab (img src works without CORS)
 * - PDFs: Google Viewer (Azure blob CORS blocks browser fetch; direct links download)
 */
export const openDocumentInNewTab = async (url, displayName = 'Document') => {
  if (!url || typeof window === 'undefined') {
    console.warn('[DocumentView] openDocumentInNewTab skipped — missing url or window');
    return;
  }

  const extension = getFileExtension(url);
  const isImage = isImageFile(url);
  const isPdf = isPdfFile(url);

  console.log('[DocumentView] View clicked', {
    displayName,
    extension,
    isImage,
    isPdf,
    url,
  });

  if (isImage) {
    openImagePreview(url, displayName);
    return;
  }

  if (isPdf) {
    // Skip fetch — Azure Blob has no CORS for localhost/admin origins, so fetch
    // always fails with console noise. Google Viewer opens the PDF in a new tab.
    openPdfInGoogleViewer(url, displayName);
    return;
  }

  const href = getDocumentViewHref(url);
  console.log('[DocumentView] other file type — opening via', href);
  window.open(href, '_blank', 'noopener,noreferrer');
};
